import { DropShadowFilter } from '@pixi/filter-drop-shadow';
import { Application, Container, Matrix, Rectangle, Sprite, Text, Texture } from 'pixi.js';
import { SmoothGraphics as Graphics } from '@pixi/graphics-smooth';

interface MessageBubbleOptions {
  cornerRadius?: number;
  tailWidth?: number;
  tailHeight?: number;
  fillColor: string[];
  strokeColor?: string;
  shadowColor?: number;
  children?: (Sprite | Text)[];
  gap?: number;
  padding?: { x: number; y: number };
  pointer?: boolean;
  nameTooltipHeight: number;
}

export const createLinearGradientTexture = (width: number, height: number, colorStops: string[]) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  colorStops?.forEach((color, index) =>
    gradient.addColorStop(index === colorStops.length - 1 ? 0.92 : index / colorStops.length, color)
  );

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  return Texture.from(canvas);
};

export class MessageBubble extends Container {
  private options: MessageBubbleOptions;
  private bubble: Graphics | null = null;
  private contentContainer: Container | null = null;
  private progressOverlay: Graphics | null = null;

  constructor(options: MessageBubbleOptions) {
    super();
    this.options = options;
    this.redraw();
  }

  updateChildren(newChildren: (Sprite | Text)[]) {
    this.options.children = newChildren;
    this.removeChildren();
    this.bubble = null;
    this.contentContainer = null;
    this.redraw();
  }

  private redraw() {
    const {
      cornerRadius = 8,
      tailWidth = 8,
      tailHeight = 6,
      fillColor = ['#777C94', '#191A20'],
      strokeColor = 0xffffff,
      shadowColor,
      children = [],
      gap = 2,
      pointer = false,
      nameTooltipHeight,
      padding = { x: 0, y: 0 }
    } = this.options;

    const px = padding.x;
    const py = padding.y;
    const width = children.reduce((acc, child) => acc + child.width + gap, 0) - gap + px * 2;
    const height = (children[0]?.height ?? 0) + py * 2 + 2;

    this.width = width;
    this.height = height;

    // Background bubble
    const bubble = new Graphics();
    const gradientTexture = createLinearGradientTexture(width, height + tailHeight, fillColor);

    bubble.lineStyle(1.5, strokeColor, 1);
    bubble.beginTextureFill({
      texture: gradientTexture,
      matrix: new Matrix()
    });

    const r = cornerRadius;
    const w = width;
    const h = height;
    const tailW = tailWidth;
    const tailH = tailHeight;

    bubble.moveTo(r, 0);
    bubble.lineTo(w - r, 0);
    bubble.quadraticCurveTo(w, 0, w, r);
    bubble.lineTo(w, h - r);
    bubble.quadraticCurveTo(w, h, w - r, h);

    const tailStartX = w / 2 + tailW / 2;
    bubble.lineTo(tailStartX, h);
    bubble.lineTo(w / 2, h + tailH);
    bubble.lineTo(w / 2 - tailW / 2, h);

    bubble.lineTo(r, h);
    bubble.quadraticCurveTo(0, h, 0, h - r);
    bubble.lineTo(0, r);
    bubble.quadraticCurveTo(0, 0, r, 0);

    bubble.endFill();

    bubble.filters = [
      new DropShadowFilter({
        color: shadowColor || 0xddcead,
        alpha: 1,
        blur: 8,
        quality: 4,
        resolution: devicePixelRatio,
        pixelSize: [0.5, 0.5],
        offset: { x: 0, y: 0 }
      })
    ];

    if (pointer) {
      this.interactive = true;
      this.cursor = 'pointer';
      this.hitArea = new Rectangle(0, 0, width, height + tailHeight + nameTooltipHeight);
    }

    this.bubble = bubble;
    this.addChild(bubble);

    const contentContainer = new Container();
    let totalWidth = 0;

    children.forEach((child, i) => {
      child.x = totalWidth;
      contentContainer.addChild(child);
      totalWidth += child.width + (i < children.length - 1 ? gap : 0);
    });

    contentContainer.x = (width - totalWidth) / 2;
    contentContainer.y = (height - contentContainer.height) / 2;

    this.contentContainer = contentContainer;
    this.addChild(contentContainer);
  }

  public setProgress(progress: number) {
    if (!this.bubble) return;

    progress = Math.min(1, Math.max(0, progress));

    if (this.progressOverlay) {
      this.removeChild(this.progressOverlay);
      this.progressOverlay.destroy();
      this.progressOverlay = null;
    }

    if (progress <= 0 || progress >= 1) {
      return;
    }

    const {
      cornerRadius = 8,
      tailWidth = 8,
      tailHeight = 6,
      children = [],
      gap = 2,
      padding = { x: 0, y: 0 }
    } = this.options;

    const px = padding.x;
    const py = padding.y;
    const width = children.reduce((acc, child) => acc + child.width + gap, 0) - gap + px * 2;
    const height = (children[0]?.height ?? 0) + py * 2 + 2;

    const r = cornerRadius;
    const w = width;
    const h = height;
    const tailW = tailWidth;
    const tailH = tailHeight;

    // Helper to sample points along an arc for a corner
    function arcPoints(
      cx: number,
      cy: number,
      startAngle: number,
      endAngle: number,
      radius: number,
      segments: number
    ): { x: number; y: number }[] {
      const points = [];
      const step = (endAngle - startAngle) / segments;
      for (let i = 0; i <= segments; i++) {
        const angle = startAngle + step * i;
        points.push({
          x: cx + Math.cos(angle) * radius,
          y: cy + Math.sin(angle) * radius
        });
      }
      return points;
    }

    const segmentsPerCorner = 8;

    // Construct full outline path points with curves (same as before)

    let pathPoints: { x: number; y: number }[] = [];

    // Top edge: from (r,0) to (w-r,0)
    pathPoints.push({ x: r, y: 0 });
    pathPoints.push({ x: w - r, y: 0 });

    // Top-right corner (270° to 360°)
    pathPoints = pathPoints.concat(arcPoints(w - r, r, Math.PI * 1.5, Math.PI * 2, r, segmentsPerCorner).slice(1));

    // Right edge
    pathPoints.push({ x: w, y: r });
    pathPoints.push({ x: w, y: h - r });

    // Bottom-right corner (0° to 90°)
    pathPoints = pathPoints.concat(arcPoints(w - r, h - r, 0, Math.PI / 2, r, segmentsPerCorner).slice(1));

    // Bottom edge before tail
    pathPoints.push({ x: w - r, y: h });
    pathPoints.push({ x: w / 2 + tailW / 2, y: h });

    // Tail
    pathPoints.push({ x: w / 2, y: h + tailH });
    pathPoints.push({ x: w / 2 - tailW / 2, y: h });

    // Bottom edge after tail
    pathPoints.push({ x: r, y: h });

    // Bottom-left corner (90° to 180°)
    pathPoints = pathPoints.concat(arcPoints(r, h - r, Math.PI / 2, Math.PI, r, segmentsPerCorner).slice(1));

    // Left edge
    pathPoints.push({ x: 0, y: h - r });
    pathPoints.push({ x: 0, y: r });

    // Top-left corner (180° to 270°)
    pathPoints = pathPoints.concat(arcPoints(r, r, Math.PI, Math.PI * 1.5, r, segmentsPerCorner).slice(1));

    // Close path (r, 0)
    pathPoints.push({ x: r, y: 0 });

    // Find the closest point index to center top (w/2, 0) along the path
    const startX = w / 2;
    const startY = 0;

    let startIndex = 0;
    let minDist = Infinity;
    for (let i = 0; i < pathPoints.length; i++) {
      const dx = pathPoints[i].x - startX;
      const dy = pathPoints[i].y - startY;
      const dist = dx * dx + dy * dy; // squared distance
      if (dist < minDist) {
        minDist = dist;
        startIndex = i;
      }
    }

    // Reorder pathPoints to start from startIndex and wrap around
    const reorderedPoints = pathPoints.slice(startIndex).concat(pathPoints.slice(0, startIndex));

    // Calculate total length of reordered path
    let totalLength = 0;
    const segmentLengths: number[] = [];
    for (let i = 0; i < reorderedPoints.length - 1; i++) {
      const dx = reorderedPoints[i + 1].x - reorderedPoints[i].x;
      const dy = reorderedPoints[i + 1].y - reorderedPoints[i].y;
      const segmentLength = Math.sqrt(dx * dx + dy * dy);
      segmentLengths.push(segmentLength);
      totalLength += segmentLength;
    }

    const targetLength = totalLength * progress;

    // Draw partial path up to targetLength
    const g = new Graphics();
    g.lineStyle(1, 0x00ff00, 0.7);

    g.moveTo(reorderedPoints[0].x, reorderedPoints[0].y);

    let lengthDrawn = 0;

    for (let i = 0; i < reorderedPoints.length - 1; i++) {
      const start = reorderedPoints[i];
      const end = reorderedPoints[i + 1];
      const segmentLength = segmentLengths[i];

      if (lengthDrawn + segmentLength <= targetLength) {
        g.lineTo(end.x, end.y);
        lengthDrawn += segmentLength;
      } else {
        const remainingLength = targetLength - lengthDrawn;
        const t = remainingLength / segmentLength;

        const ix = start.x + (end.x - start.x) * t;
        const iy = start.y + (end.y - start.y) * t;

        g.lineTo(ix, iy);
        break;
      }
    }

    this.progressOverlay = g;
    this.addChild(g);
  }
}

type TTadaOptions = {
  duration?: number; // Full loop duration (ms)
  delay?: number; // Time between loops
  scaleAmplitude?: number;
  rotateAmplitude?: number; // in degrees
};

export const applyTadaLoop = (sprite: Sprite, app: Application, options: TTadaOptions = {}) => {
  const duration = options.duration ?? 1000;
  const delay = options.delay ?? 1500;
  const scaleAmp = options.scaleAmplitude ?? 0.1;
  const rotateAmpDeg = options.rotateAmplitude ?? 3;
  const rotateAmpRad = (rotateAmpDeg * Math.PI) / 180;

  let state: 'animating' | 'waiting' = 'animating';
  let startTime = performance.now();

  const animate = () => {
    const now = performance.now();
    const elapsed = now - startTime;

    if (state === 'animating') {
      const t = (elapsed % duration) / duration; // Normalized time [0, 1]

      // Scale oscillates from 0.9 → 1.1 → 1
      const scale = 1 + scaleAmp * Math.sin(t * Math.PI * 10);
      const rotation = rotateAmpRad * Math.sin(t * Math.PI * 20);

      sprite.scale.set(scale, scale);
      sprite.rotation = rotation;

      if (elapsed >= duration) {
        state = 'waiting';
        startTime = now;
      }
    } else if (state === 'waiting') {
      sprite.scale.set(1, 1);
      sprite.rotation = 0;
      if (elapsed >= delay) {
        state = 'animating';
        startTime = now;
      }
    }
  };

  app.ticker.add(animate);
  sprite.on('destroyed', () => app.ticker.remove(animate));
};

type TWobbleLoopOptions = {
  duration?: number; // total time of wobble cycle
  delay?: number; // delay before next loop
  moveAmplitude?: number; // pixels to shift left/right
  rotateAmplitude?: number; // degrees to rotate left/right
};

export const applyWobbleLoop = (sprite: Sprite, app: Application, options: TWobbleLoopOptions = {}) => {
  const duration = options.duration ?? 1000;
  const delay = options.delay ?? 2000;
  const moveAmp = options.moveAmplitude ?? 8; // pixels
  const rotateAmpDeg = options.rotateAmplitude ?? 15;
  const rotateAmpRad = (rotateAmpDeg * Math.PI) / 180;

  const originalX = sprite.x;
  const originalRotation = sprite.rotation;

  let state: 'animating' | 'waiting' = 'waiting';
  let startTime = performance.now();

  const animate = () => {
    const now = performance.now();
    const elapsed = now - startTime;

    if (state === 'animating') {
      const t = Math.min(elapsed / duration, 1); // 0 → 1
      const decay = 1 - t; // damping toward end

      // mimic wobble: 6 swings (like CSS wobble)
      const swing = Math.sin(t * Math.PI * 6);

      sprite.x = originalX + swing * moveAmp * decay;
      sprite.rotation = originalRotation + swing * rotateAmpRad * decay;

      if (t >= 1) {
        state = 'waiting';
        startTime = now;
        sprite.x = originalX;
        sprite.rotation = originalRotation;
      }
    } else if (state === 'waiting' && elapsed >= delay) {
      state = 'animating';
      startTime = now;
    }
  };

  sprite.on('destroyed', () => app.ticker.remove(animate));

  const stop = () => {
    app.ticker.remove(animate);
    sprite.x = originalX;
    sprite.rotation = originalRotation;
  };

  const start = () => {
    if (state === 'animating') {
      return;
    }
    if (state === 'waiting') {
      state = 'animating';
      startTime = performance.now();
    }
    app.ticker.add(animate);
  };

  return { stop, start };
};
